
import React, { useRef, useState } from 'react';
import { CalendarEvent } from '../../types';
import { Clock, ArrowLeftRight, X, Copy, Trash2 } from 'lucide-react';
import { fromLocalISOString } from '@/utils/timezoneUtils';
import { 
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator 
} from '@/components/ui/context-menu';
import { format } from 'date-fns';

interface CalendarEventItemProps {
  event: CalendarEvent;
  index: number;
  handleEventClick: (event: CalendarEvent, e: React.MouseEvent) => void;
  onDuplicateEvent?: (event: CalendarEvent) => void;
  onDeleteEvent?: (event: CalendarEvent) => void;
  formatGuardNames: (guardNames?: string[]) => string;
  onCopyShift?: (event: CalendarEvent) => void;
  onCopyShiftNoGuards?: (event: CalendarEvent) => void;
  onMarkForSwap?: (event: CalendarEvent) => void;
  onSwapWithMarked?: (event: CalendarEvent) => void;
  onCancelSwap?: () => void;
  isMarkedForSwap?: boolean;
  hasMarkedShift?: boolean;
  titleColor?: { backgroundColor: string; textColor: string };
  canUpdate?: boolean;
  canDelete?: boolean;
  isMultiSelectMode?: boolean;
  isSelected?: boolean;
  onEventSelect?: (eventId: string) => void;
  hasMultipleShifts?: boolean;
  isPerJobView?: boolean;
  translations?: {
    guards: string;
    duplicate: string;
    delete: string;
    copy: string;
    copyWithoutGuards: string;
    paste: string;
  };
}

const CalendarEventItem = ({
  event,
  index,
  handleEventClick,
  onDuplicateEvent,
  onDeleteEvent,
  formatGuardNames,
  onCopyShift,
  onCopyShiftNoGuards,
  onMarkForSwap,
  onSwapWithMarked,
  onCancelSwap,
  isMarkedForSwap = false,
  hasMarkedShift = false,
  titleColor,
  canUpdate = false,
  canDelete = false,
  isMultiSelectMode = false,
  isSelected = false,
  onEventSelect,
  hasMultipleShifts = false,
  isPerJobView = false,
  translations = {
    guards: 'Guards',
    duplicate: 'Duplicate',
    delete: 'Delete',
    copy: 'Copy Shift',
    copyWithoutGuards: 'Copy Without Guards',
    paste: 'Paste'
  }
}: CalendarEventItemProps) => {
  const eventRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleClick = (e: React.MouseEvent) => {
    if (isMultiSelectMode && onEventSelect) {
      e.stopPropagation();
      onEventSelect(event.id);
    } else {
      handleEventClick(event, e);
    }
  };

  // Use provided color or fallback
  const backgroundColor = titleColor ? titleColor.backgroundColor : '#3f88c5';
  const textColor = titleColor ? titleColor.textColor : 'white';

  // Helper function to format name with last name first (the data is already in correct format)
  const formatNameWithLastNameFirst = (name: string) => {
    // The guard data already comes in "LAST_NAME FIRST_NAME" format, so just return as is
    return name;
  };

  const getGuardNames = (): string => {
    console.log('=== CALENDAR EVENT ITEM DEBUG ===');
    console.log('Event ID:', event.id);
    console.log('Event title:', event.title);
    console.log('Event guard_names:', event.guard_names);
    console.log('Event assigned_guards:', event.assigned_guards);
    console.log('Global guards available:', !!(window as any)?.guards);
    
    // Always use the guards data from global window object for proper formatting
    // Skip event.guard_names as it may have old format names
    const guards = (window as any)?.guards || [];
    
    // Try assigned_guards if guard_names is not available
    if (event.assigned_guards && Array.isArray(event.assigned_guards) && event.assigned_guards.length > 0) {
      console.log('CalendarEventItem - Processing assigned_guards:', event.assigned_guards);
      
      // Check if assigned_guards contains guard objects with name properties
      const firstGuard = event.assigned_guards[0];
      if (firstGuard && typeof firstGuard === 'object' && firstGuard !== null) {
        const guardObj = firstGuard as Record<string, any>;
        if ('name' in guardObj || 'full_name' in guardObj || 'first_name' in guardObj || 'last_name' in guardObj) {
          const guardNames = event.assigned_guards
            .map((guard: any) => {
              if (guard && typeof guard === 'object') {
                // Try different name combinations
                if (guard.full_name) {
                  return formatNameWithLastNameFirst(guard.full_name);
                }
                
                if (guard.first_name && guard.last_name) {
                  return `${guard.last_name}, ${guard.first_name}`;
                }
                
                if (guard.name) {
                  return formatNameWithLastNameFirst(guard.name);
                }
                
                return guard.first_name || guard.last_name || 'Unknown Guard';
              }
              return 'Unknown Guard';
            })
            .filter(name => name !== 'Unknown Guard');
          
          if (guardNames.length > 0) {
            return guardNames.join(', ');
          }
        }
      }
      
      // If assigned_guards contains just IDs, try to match with guards data
      if (guards && guards.length > 0) {
        console.log('CalendarEventItem - Matching guard IDs with global guards data');
        const guardNames = event.assigned_guards
          .map((guardId: string) => {
            if (typeof guardId === 'string') {
              const guard = guards.find((g: any) => g.id === guardId);
              console.log(`CalendarEventItem - Looking for guard ID ${guardId}:`, guard);
              if (guard) {
                if (guard.full_name) {
                  return formatNameWithLastNameFirst(guard.full_name);
                }
                if (guard.first_name && guard.last_name) {
                  return `${guard.last_name}, ${guard.first_name}`;
                }
                if (guard.name) {
                  return formatNameWithLastNameFirst(guard.name);
                }
                return guard.first_name || guard.last_name || `Guard ${guardId.slice(0, 8)}`;
              }
            }
            return null;
          })
          .filter(Boolean);
        
        if (guardNames.length > 0) {
          return guardNames.join(', ');
        }
      }
      
      // If we still don't have names, show count only if we actually have assigned guards
      return `${event.assigned_guards.length} guard(s)`;
    }
    
    return 'No guards assigned';
  };

  const guardNames = getGuardNames();
  const hasGuards = guardNames !== 'No guards assigned' && !guardNames.includes('guard(s)');
  
  // Check if shift has multiple employees
  const hasMultipleEmployees = () => {
    if (event.guard_names && Array.isArray(event.guard_names)) {
      return event.guard_names.filter(name => name && typeof name === 'string' && name.trim() !== '').length > 1;
    }
    if (event.assigned_guards && Array.isArray(event.assigned_guards)) {
      return event.assigned_guards.length > 1;
    }
    return false;
  };

  const multipleEmployees = hasMultipleEmployees();
  
  // Calculate shift duration in hours
  const calculateShiftDuration = () => {
    const startDate = fromLocalISOString(event.start_date);
    const endDate = fromLocalISOString(event.end_date);
    const durationMs = endDate.getTime() - startDate.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);
    
    // Format to fixed 1 decimal place if not a whole number
    return durationHours % 1 === 0 
      ? `${durationHours}hrs` 
      : `${durationHours.toFixed(1)}hrs`;
  };

  // Diagonal line pattern for multiple shifts
  const diagonalPattern = hasMultipleShifts ? {
    backgroundImage: `repeating-linear-gradient(45deg, 
      rgba(139, 92, 246, 0.15), 
      rgba(139, 92, 246, 0.15) 5px, 
      transparent 5px, 
      transparent 15px)`
  } : {};

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div 
          ref={eventRef}
          className={`calendar-event relative mb-1 p-2 text-sm rounded overflow-hidden cursor-pointer transition-colors min-h-[90px] flex flex-col justify-between
          ${isMultiSelectMode ? 'hover:brightness-90' : 'hover:brightness-95'}
          ${isSelected ? 'ring-2 ring-primary ring-offset-1' : ''}
          ${hasMultipleShifts ? 'ring-2 ring-purple-500 ring-offset-1' : ''}
          ${isMarkedForSwap ? 'ring-4 ring-amber-500 ring-offset-2 animate-pulse' : ''}
          `}
          style={{
            backgroundColor,
            color: textColor,
            ...diagonalPattern
          }}
          onClick={handleClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          data-event-id={event.id}
        >
          {/* Multiple employees watermark - positioned on left to avoid delete button */}
          {multipleEmployees && (
            <div className="absolute top-0.5 left-0.5 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold shadow-lg z-20 border border-white">
              {Array.isArray(event.guard_names) ? event.guard_names.filter(name => name && typeof name === 'string' && name.trim() !== '').length : 
               Array.isArray(event.assigned_guards) ? event.assigned_guards.length : 2}
            </div>
          )}
          {/* Time range and duration in same row */}
          <div className="text-xs leading-tight font-medium flex items-center justify-between mb-1 flex-shrink-0">
            <div className="whitespace-nowrap flex-shrink-0">
              {format(fromLocalISOString(event.start_date), 'HH:mm')} - {format(fromLocalISOString(event.end_date), 'HH:mm')}
            </div>
            <div className="flex items-center gap-1 ml-2 flex-shrink-0">
              <Clock className="h-3 w-3" />
              <span className="text-xs leading-tight whitespace-nowrap">{calculateShiftDuration()}</span>
            </div>
          </div>

          {/* In standard view (not per-job): Show shift title prominently */}
          {!isPerJobView && (
            <div className="text-xs leading-tight font-semibold mb-1 flex-1 min-h-[40px] flex items-center overflow-hidden">
              <div className="break-words whitespace-normal w-full overflow-hidden text-ellipsis">
                {event.title}
              </div>
            </div>
          )}

          {/* In per-job view: Show shift title prominently */}
          {isPerJobView && (
            <div className="text-xs leading-tight font-semibold mb-1 flex-1 min-h-[40px] flex items-center overflow-hidden">
              <div className="break-words whitespace-normal w-full overflow-hidden text-ellipsis">
                {event.title}
              </div>
            </div>
          )}
          
          {/* In standard view (not per-job): Show guard names in orange box */}
          {!isPerJobView && (
            <div className="text-xs leading-tight rounded-md bg-orange-400 text-white p-1 px-2 font-medium break-words whitespace-normal flex-shrink-0 mt-auto overflow-hidden text-ellipsis">
              {guardNames}
            </div>
          )}

          {/* In per-job view: Show team name as secondary info */}
          {isPerJobView && event.team_name && (
            <div className="text-xs leading-tight opacity-80 break-words whitespace-normal flex-shrink-0 mt-auto overflow-hidden text-ellipsis">
              {String(event.team_name)}
            </div>
          )}
          
          {showTooltip && (
            <div 
              className="absolute left-0 top-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded p-2 z-50 text-gray-800 dark:text-gray-100 text-xs"
              style={{ minWidth: '200px' }}
            >
              <div className="font-medium mb-1">{event.title}</div>
              <div className="mb-1">
                <span className="font-medium mr-1">Time:</span>
                {format(fromLocalISOString(event.start_date), 'HH:mm')} - {format(fromLocalISOString(event.end_date), 'HH:mm')}
              </div>
              <div className="mb-1">
                <span className="font-medium mr-1">Duration:</span>
                {calculateShiftDuration()}
              </div>
              {hasGuards && (
                <div className="mb-1">
                  <span className="font-medium mr-1">{translations.guards}:</span>
                  {guardNames}
                </div>
              )}
              
              {/* Add indication for multiple shifts */}
              {hasMultipleShifts && (
                <div className="mb-1 text-purple-600 font-medium">
                  Guard has multiple shifts on this day
                </div>
              )}
            </div>
          )}
        </div>
      </ContextMenuTrigger>
      
      <ContextMenuContent className="w-48">
        {canUpdate && onMarkForSwap && !hasMarkedShift && (
          <>
            <ContextMenuItem onClick={() => onMarkForSwap(event)}>
              <ArrowLeftRight className="mr-2 h-4 w-4" />
              Mark for Swap
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        
        {canUpdate && onSwapWithMarked && hasMarkedShift && !isMarkedForSwap && (
          <>
            <ContextMenuItem onClick={() => onSwapWithMarked(event)} className="font-bold text-amber-600">
              <ArrowLeftRight className="mr-2 h-4 w-4" />
              Swap with Marked Shift
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        
        {canUpdate && onCancelSwap && isMarkedForSwap && (
          <>
            <ContextMenuItem onClick={onCancelSwap} className="text-destructive">
              <X className="mr-2 h-4 w-4" />
              Cancel Swap
            </ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        
        {canUpdate && onDuplicateEvent && (
          <ContextMenuItem onClick={() => onDuplicateEvent(event)}>
            <Copy className="mr-2 h-4 w-4" />
            {translations.duplicate}
          </ContextMenuItem>
        )}
        
        {onCopyShift && (
          <ContextMenuItem onClick={() => onCopyShift(event)}>
            <Copy className="mr-2 h-4 w-4" />
            {translations.copy}
          </ContextMenuItem>
        )}

        {onCopyShiftNoGuards && (
          <ContextMenuItem onClick={() => onCopyShiftNoGuards(event)}>
            <Copy className="mr-2 h-4 w-4" />
            {translations.copyWithoutGuards}
          </ContextMenuItem>
        )}
        
        {(canUpdate || canDelete) && <ContextMenuSeparator />}
        
        {canDelete && onDeleteEvent && (
          <ContextMenuItem className="text-destructive" onClick={() => onDeleteEvent(event)}>
            <Trash2 className="mr-2 h-4 w-4" />
            {translations.delete}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
};

export default CalendarEventItem;
